import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

/**
 * Full-screen photo viewer.
 *
 * Rendered via a React portal to document.body so no ancestor
 * `transform` / `filter` / `backdrop-filter` / `perspective` /
 * `will-change` / `contain` can turn into its containing block.
 * Framer Motion's PageTransition applies `filter: blur(0px)` on the
 * route wrapper, which otherwise traps every `position: fixed` child
 * inside the wrapper's bounding box and renders the lightbox mostly
 * off-screen. Portalling to <body> sidesteps that entirely.
 *
 * - Previous/next arrows + keyboard navigation
 * - Swipe navigation on touch devices
 * - Double-tap or pinch to zoom
 * - Optional caption
 * - Close button, Escape, or click outside to close
 * - Graceful fallback if the image fails to load — close controls
 *   stay reachable, scroll is still restored.
 */
export default function PhotoLightbox({ items, index, onClose, onNavigate }) {
  const [zoomed, setZoomed] = useState(false);
  const [failed, setFailed] = useState(false);
  const touchStart = useRef(null);

  const safeItems = Array.isArray(items) ? items : [];
  const item = safeItems[index] || {};
  const url = typeof item === "string" ? item : item.url;
  const caption = typeof item === "string" ? "" : item.caption || "";
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const fullUrl = url && url.startsWith("http") ? url : `${backendUrl}${url || ""}`;

  // Reset per-image state when the user navigates to a different photo.
  useEffect(() => {
    setZoomed(false);
    setFailed(false);
  }, [index]);

  // Keyboard + scroll lock. The cleanup ALWAYS restores body scroll,
  // even if the component unmounts because of an upstream error.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onNavigate(Math.max(0, index - 1));
      else if (e.key === "ArrowRight")
        onNavigate(Math.min(safeItems.length - 1, index + 1));
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, safeItems.length, onClose, onNavigate]);

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0];
  };
  const onTouchEnd = (e) => {
    if (zoomed || !touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.clientX;
    const dy = e.changedTouches[0].clientY - touchStart.current.clientY;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0 && index < safeItems.length - 1) onNavigate(index + 1);
      else if (dx > 0 && index > 0) onNavigate(index - 1);
    }
    touchStart.current = null;
  };

  // Only portal on the client — avoids SSR / build-time crashes.
  if (typeof document === "undefined") return null;

  const node = (
    <div
      data-testid="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      className="fixed inset-0 z-[2147483646] bg-black/92 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        data-testid="lightbox-close"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close photo viewer"
        className="absolute top-4 right-4 text-white hover:text-white/90 transition-colors bg-black/50 hover:bg-black/70 rounded-full p-2 z-[2147483647]"
      >
        <X size={22} />
      </button>

      {index > 0 && (
        <button
          data-testid="lightbox-prev"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index - 1);
          }}
          aria-label="Previous photo"
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-white/90 bg-black/50 hover:bg-black/70 rounded-full p-3 z-[2147483647]"
        >
          <ChevronLeft size={26} />
        </button>
      )}
      {index < safeItems.length - 1 && (
        <button
          data-testid="lightbox-next"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index + 1);
          }}
          aria-label="Next photo"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-white/90 bg-black/50 hover:bg-black/70 rounded-full p-3 z-[2147483647]"
        >
          <ChevronRight size={26} />
        </button>
      )}

      <div
        onDoubleClick={() => setZoomed((z) => !z)}
        className="w-full h-full flex items-center justify-center p-4 sm:p-10"
      >
        {failed ? (
          <div
            data-testid="lightbox-error"
            onClick={(e) => e.stopPropagation()}
            className="text-white/90 text-center font-body text-sm sm:text-base max-w-md"
          >
            <p className="mb-2">We couldn&apos;t load this photo.</p>
            <p className="text-white/60 text-xs sm:text-sm">
              Tap <span className="font-semibold">Close</span>, press <kbd>Esc</kbd>,
              or tap anywhere outside to return to the page.
            </p>
          </div>
        ) : (
          <img
            src={fullUrl}
            alt={caption || "photo"}
            onClick={(e) => e.stopPropagation()}
            className={`select-none ${
              zoomed
                ? "max-w-none max-h-none cursor-zoom-out"
                : "max-w-full max-h-full object-contain cursor-zoom-in"
            }`}
            style={{ touchAction: "pinch-zoom" }}
            draggable={false}
            onError={() => setFailed(true)}
          />
        )}
      </div>

      {caption && !failed && (
        <div className="absolute bottom-16 left-0 right-0 text-center px-6 pointer-events-none">
          <p className="inline-block text-white/90 text-sm sm:text-base bg-black/40 px-4 py-2 rounded-full">
            {caption}
          </p>
        </div>
      )}
      <div className="absolute bottom-6 left-0 right-0 text-center text-white/70 text-xs pointer-events-none">
        {index + 1} of {safeItems.length} ·{" "}
        <ZoomIn size={12} className="inline" /> double-tap or pinch to zoom
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
